import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Clock } from 'lucide-react';
import { Callout } from '../../components/workbook/Callout';
import { getPhaseColor, getPhaseDescription } from '../../lib/workbook/utils';

const engagePages = [
  { number: 1, title: 'Purpose & Desired Outcomes', description: 'Start with why before what', duration: '10 min' },
  { number: 2, title: 'Stakeholder Map', description: 'Who matters and why', duration: '10 min' },
  { number: 3, title: 'Stakeholder Wins', description: 'Make needs explicit', duration: '10 min' },
  { number: 4, title: 'Constraints & Risks', description: 'See it before it stings', duration: '10-15 min' },
  { number: 5, title: 'Ways of Working', description: 'How we\'ll actually operate', duration: '10 min' },
  { number: 6, title: 'Play It Out', description: 'Build influence network', duration: '10-15 min' },
  { number: 7, title: 'The Engagement Brief', description: 'One-pager to share', duration: '10-20 min' },
  { number: 8, title: 'Facilitation Rails', description: 'Retrieval check', duration: '5 min' },
];

export default function EngagePhase() {
  const phaseColor = getPhaseColor('engage', 600);
  const phaseBg = getPhaseColor('engage', 50);

  return (
    <div className="min-h-screen bg-[var(--bg-raised)]">
      {/* Header */}
      <div className="border-b" style={{ backgroundColor: phaseBg, borderColor: phaseColor + '40' }}>
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center text-white text-xl font-bold" style={{ backgroundColor: phaseColor }}>
              E
            </div>
            <div>
              <h1 className="text-3xl font-bold" style={{ color: phaseColor }}>Engage</h1>
              <p style={{ color: phaseColor + 'DD' }}>{getPhaseDescription('engage')}</p>
            </div>
          </div>
          <p className="max-w-3xl" style={{ color: phaseColor + 'CC' }}>
            Align on purpose, people, and constraints before diving into options. 
            This phase ensures everyone understands the why, who, and what's possible 
            before making decisions.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Phase Overview */}
            <div className="bg-[var(--surface)] p-6">
              <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">
                Phase Overview
              </h2>
              <div className="space-y-4">
                <p className="text-[var(--text-sec)]">
                  Before generating options, align on purpose, people, and constraints. 
                  This phase helps you:
                </p>
                <ul className="space-y-2 text-[var(--text-sec)]">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: phaseColor }} />
                    <span>Clarify desired outcomes and success signals</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: phaseColor }} />
                    <span>Map stakeholders and their needs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: phaseColor }} />
                    <span>Surface constraints and risks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: phaseColor }} />
                    <span>Agree on ways of working</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Pages List */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-[var(--text-pri)]">
                Pages in This Phase
              </h2>
              {engagePages.map((page) => (
                <Link
                  key={page.number}
                  to={`/elevation/workbook/engage/page-${page.number}`}
                  className="block p-4 border border-[var(--rule)] hover:border-[var(--slate)] hover:bg-[var(--slate-dim)] transition-colors group"
                  style={{ borderColor: phaseColor + '40' }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ backgroundColor: phaseColor }}>
                        {page.number}
                      </div>
                      <div>
                        <h3 className="font-semibold text-[var(--text-pri)] group-hover:text-[var(--text-ter)]">
                          {page.title}
                        </h3>
                        <p className="text-sm text-[var(--text-sec)]">
                          {page.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[var(--text-ter)]">
                      <Clock className="w-4 h-4" />
                      {page.duration}
                      <ArrowRight className="w-4 h-4 group-hover:text-[var(--text-ter)]" style={{ color: phaseColor }} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Key Concepts */}
            <Callout type="why" phase="engage">
              <div className="space-y-2">
                <p className="font-semibold">Key Concepts:</p>
                <ul className="space-y-1 text-sm">
                  <li>• <strong>Purpose:</strong> Start with why before what</li>
                  <li>• <strong>People:</strong> Map stakeholders and their needs</li>
                  <li>• <strong>Constraints:</strong> Surface risks early to avoid surprises</li>
                  <li>• <strong>Alignment:</strong> Shared understanding enables better decisions</li>
                </ul>
              </div>
            </Callout>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Start */}
            <div className="p-4 border" style={{ backgroundColor: phaseBg, borderColor: phaseColor + '40' }}>
              <h3 className="font-semibold mb-3" style={{ color: phaseColor + 'CC' }}>
                Quick Start
              </h3>
              <div className="space-y-2 text-sm" style={{ color: phaseColor + 'AA' }}>
                <p>1. Define desired outcomes</p>
                <p>2. Map stakeholders</p>
                <p>3. Identify constraints</p>
                <p>4. Agree on ways of working</p>
              </div>
            </div>

            {/* Timing Guide */}
            <div className="bg-[var(--surface)] border border-[var(--rule)] p-4">
              <h3 className="font-semibold text-[var(--text-pri)] mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Timing Guide
              </h3>
              <div className="space-y-2 text-sm text-[var(--text-sec)]">
                <div className="flex justify-between">
                  <span>Solo work:</span>
                  <span className="font-medium">~80 min</span>
                </div>
                <div className="flex justify-between">
                  <span>Team session:</span>
                  <span className="font-medium">~120 min</span>
                </div>
                <div className="flex justify-between">
                  <span>Coach workshop:</span>
                  <span className="font-medium">~90 min</span>
                </div>
              </div>
            </div>

            {/* Success Criteria */}
            <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4">
              <h3 className="font-semibold text-[var(--text-sec)] mb-3">
                Success Criteria
              </h3>
              <div className="space-y-2 text-sm text-[var(--text-sec)]">
                <p>✓ Clear purpose and outcomes</p>
                <p>✓ Stakeholder map complete</p>
                <p>✓ Constraints identified</p>
                <p>✓ Engagement brief published</p>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-12 flex justify-between">
          <Link
            to="/elevation/workbook/home"
            className="px-4 py-2 text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
          >
            ← Back to Overview
          </Link>
          <Link
            to="/elevation/workbook/engage/page-1"
            className="inline-flex items-center gap-2 text-white px-6 py-3 font-semibold hover:opacity-90 transition-colors"
            style={{ backgroundColor: phaseColor }}
          >
            Start Engage Phase
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}

